import { Button } from '../components/Button.js'
import { KeyCap } from '../components/KeyCap.js'

export interface PlayAgainProps {
  /** The button, for the devices that have something to press it with. */
  readonly label: string
  /**
   * How the television's sentence ends: "Press Enter **to play again**".
   *
   * Its own words rather than the label reused, because "Press Enter another
   * go" is not a sentence. Two short strings per game is the honest cost of
   * two different things being said in two different places.
   */
  readonly does: string
  readonly onAgain: () => void
}

/**
 * How to start the next one, said the way the room can actually hear it.
 *
 * Every game ended with a button. On a phone or a tablet that is the only way
 * to start another, and it stays. On the television it is a button on a device
 * with **no mouse and no touchscreen** — a control nobody can operate, offered
 * at the one moment the screen has nothing else to say. Small Rabbit's
 * mid-round buttons were dropped at this scale for exactly that reason, and
 * the end-of-round one was missed in all seven games.
 *
 * So the television gets the instruction instead. Enter has always worked —
 * every game listens for it and the legend along the bottom says so — but it
 * did not say so *here*, at the moment she is looking for what to do next,
 * which is where an instruction earns its place.
 *
 * **One component rather than seven copies.** The seven blocks this replaces
 * were identical but for their wording, and seven copies of a rule is how one
 * comes to disagree: this app has already shipped a legend offering a key that
 * did nothing, and a television drawing a board by a rule of its own.
 */
export function PlayAgain({ label, does, onAgain }: PlayAgainProps) {
  return (
    <>
      <Button onClick={onAgain} className="[[data-scale=tv]_&]:hidden">
        {label}
      </Button>

      {/*
        A sentence, not a button that only looks like text. There is nothing to
        click it with, so anything button-shaped here would be inviting a
        gesture the room does not have.
      */}
      <p className="text-ink-muted hidden flex-wrap items-center justify-center gap-2 text-(length:--text-body) [[data-scale=tv]_&]:flex">
        <span>Press</span>
        <KeyCap>Enter</KeyCap>
        <span>{does}</span>
      </p>
    </>
  )
}
